<template>
  <div class="gm-step gm-step3">
    <section class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campains_step3_send_to_people_who') }}</h2>
      <p>Choose a segment, a list or a tag. Add more than one to reach everyone who matches any of them.</p>
      <div class="gm-step3__picker">
        <gd-dropdown
          class="gm-dropdown gm-step3__type"
          :placeholder="$t('frontend_select')"
          :value="currentIncludeType"
          :options="includedElementsSelect"
          @input="chooseIncludeType"
          variant="base"
        />
        <div
          class="gm-step3__targets"
          :class="{ 'cursor-not-allowed': !currentIncludeType }"
          :title="
            !currentIncludeType
              ? $t('frontend_groovemail_mail_choose_option_from_left_menu')
              : ''
          "
        >
          <treeselect
            :key="1"
            class="custom-treeselect treeselect-add-new"
            :class="{ 'pointer-events-none': !currentIncludeType }"
            v-model="localModel.includedElements"
            :multiple="true"
            :options="includeData"
            :closeOnSelect="true"
            :clearOnSelect="true"
            :clearable="false"
            openDirection="bottom"
            :disabled="!currentIncludeType"
            @select="elementSelected"
            @deselect="elementRemoved"
            :placeholder="$t('frontend_select')"
          />
        </div>
      </div>
      <div
        v-if="localModel.subscribeoption && localModel.subscribeoption.length"
        class="gm-inline-actions"
      >
        <gs-button
          variant="outline"
          size="sm"
          @click="openCreateSegmentModal(localModel.subscribeoption)"
        >
          <gs-icon name="plus" size="16"></gs-icon>
          {{ $t('frontend_groovemail_broadcasts_create_custom_segment') }}
        </gs-button>
      </div>
    </section>

    <section class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campains_step3_estimated_recipients') }}</h2>
      <p v-if="0 < localModel.remainingEmails || localModel.remainingEmails === -1">
        {{ $t('frontend_groovemail_campains_step3_campaign_would_be_sent') }}
        <strong class="gm-step3__count">{{
          localModel.filledemails >= localModel.remainingEmails &&
          localModel.remainingEmails !== -1
            ? localModel.remainingEmails
            : localModel.filledemails
        }} {{ $t('frontend_groovemail_campains_step3_people') }}.</strong>
      </p>
      <p v-else class="gm-step3__limit-warning">
        {{ $t('frontend_groovemail_segment_subscribe_remaining_count_less_message') }}
      </p>
      <div class="gm-stat-grid">
        <div class="gm-stat">
          <LeadsIcon class="gm-stat__icon"/>
          <span class="gm-stat__label">{{ $t('frontend_groovemail_campains_step3_leads_selected') }}</span>
          <strong class="gm-stat__value">{{ localModel.totalleads }}</strong>
        </div>
        <div class="gm-stat">
          <ActiveIcon class="gm-stat__icon"/>
          <span class="gm-stat__label">{{ $t('frontend_groovemail_campains_step3_emails_filled_in') }}</span>
          <strong class="gm-stat__value">{{ localModel.filledemails }}</strong>
        </div>
        <div class="gm-stat">
          <InactiveIcon class="gm-stat__icon"/>
          <span class="gm-stat__label">{{ $t('frontend_groovemail_campains_step3_blocked') }}</span>
          <strong class="gm-stat__value">{{ localModel.blocked }}</strong>
        </div>
        <div class="gm-stat">
          <PaperIcon class="gm-stat__icon"/>
          <span class="gm-stat__label">{{ $t('frontend_groovemail_campains_step3_total_in_segment') }}</span>
          <strong class="gm-stat__value">{{ localModel.totalsend }}</strong>
        </div>
        <div class="gm-stat">
          <PaperIcon class="gm-stat__icon"/>
          <span class="gm-stat__label">{{ $t('frontend_groovemail_segment_subscribe_remaining_email_send_count') }}</span>
          <strong class="gm-stat__value" v-if="localModel.remainingEmails === -1">
            {{ $t('frontend_groovemail_plan_limit_unlimited_count_text') }}
          </strong>
          <strong class="gm-stat__value" v-else>{{ localModel.remainingEmails }}</strong>
        </div>
      </div>
    </section>

    <gs-named-modal
      name="create-maillist-in-campaign-modal"
      width="600px"
      max-width="100%"
      :title="$t('frontend_groovemail_contacts_step4_create_new_list')"
    >
      <create-list
        :type="'add'"
        :mail_list_id="null"
        :mail_list_data="mail_list_data"
        @showLoader="showLoader"
        @hideLoader="hideLoader"
        @fetchEventsList="fetchEventsList"
        @closeCreateMaillistModal="closeCreateMaillist"
      >
      </create-list>
    </gs-named-modal>

    <gs-named-modal
      name="create-mailtag-in-campaign-modal"
      width="600px"
      max-width="100%"
      :title="$t('frontend_groovemail_contacts_step4_create_newtag')"
    >
      <create-tag
        :type="'add'"
        :mail_tag_id="null"
        :mail_tag_data="mail_tag_data"
        @showLoader="showLoader"
        @hideLoader="hideLoader"
        @fetchTags="fetchEventsTags"
        @closeCreateTagsModal="closeCreateMailtag"
      ></create-tag>
    </gs-named-modal>

    <gs-named-modal
      id="create-segment-wrapper"
      name="create-segment-in-campaign-modal"
      width="770px"
      max-width="100%"
      :title="$t('frontend_groovemail_segment_new_segment')"
    >
      <div class="gm-modal-body">
        <gs-field :label="$t('frontend_groovemail_mail_tag_name')">
          <text-input
            class="gm-input"
            variant="base"
            v-model="segmentModel.segmentName"
            :placeholder="$t('frontend_groovemail_adminsetting_name')"
            :validator="$v.segmentModel.segmentName"
          />
        </gs-field>
        <div class="gm-step3__segment-builder">
          <create-segment
            ref="createFreshSegment"
            source="broadcast"
            :includedConditions="includedConditions"
            :includeType="includeType"
            :excludedConditions="excludedConditions"
            :excludeType="excludeType"
            @enableCreateSegment="enableCreateSegment"
          >
          </create-segment>
        </div>
        <div class="gm-modal-actions">
          <gs-button
            variant="outline"
            @click="closeCreateSegmentModal()"
          >{{ $t('frontend_cancel') }}</gs-button>
          <gs-button
            variant="primary"
            :disabled="!canCreateSegment"
            @click="createNewSegment()"
          >{{ $t('frontend_groovemail_segment_create_segment') }}</gs-button>
        </div>
      </div>
    </gs-named-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Treeselect from '@riophae/vue-treeselect';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';

import Mailsegment from '@/models/Mailsegment';
import Mailtag from '@/models/Mailtag';
import themeConfig from '@/theme/themeConfig.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

import showAlert from '@/components/sweet-alert-popup.js';
import CreateList from '@/components/create-lists.vue';
import CreateTag from '@/components/create-tags.vue';
import CreateSegment from '../segments/subscribe.vue';

// import icons
import LeadsIcon from '@/assets/images/icons/new/leads_new.svg';
import ActiveIcon from '@/assets/images/icons/new/email_new.svg';
import InactiveIcon from '@/assets/images/icons/new/inactive_new.svg';
import PaperIcon from '@/assets/images/icons/new/paper_new.svg';
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';

export default {
  name: 'step3',
  props: {
    model: {
      required: true,
    },
  },

  data() {
    return {
      changesMadeInStep: false,
      localModel: this.model,
      lists: [],
      segments: [],
      tags: [],
      elements: [],
      loaderColor: themeConfig.appPrimaryColor,
      isLoading: false,
      fullPageLoader: true,
      mail_list_data: { name: '', description: '' },
      currentIncludeType: '',
      mail_tag_data: {
        name: '',
        description: '',
        category: [],
        categoryname: '',
      },
      segmentModel: {
        segmentName: '',
      },
      canCreateSegment: false,
      includedConditions: [],
      includeType: 'AND',
      excludedConditions: [],
      excludeType: 'AND',
      segmentCreationCustom: false,
    };
  },

  validations: {
    localModel: {
      subscribeoption: {
        required,
      },
    },
    segmentModel: {
      segmentName: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  methods: {
    async getListsData() {
      this.showLoader();
      try {
        const mailLists = await axios.get(`/maillistonly`);
        this.lists = mailLists?.data?.data;
        this.hideLoader();
      } catch (error) {
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    async getTagsData() {
      this.showLoader();
      try {
        this.tags = await Mailtag.$get();
        this.hideLoader();
      } catch (error) {
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    async getSegmentsData() {
      this.showLoader();
      try {
        const segmentsData = await axios.get('/listing/mailsegment');
        this.segments =
          segmentsData && segmentsData.data && segmentsData.data.length
            ? segmentsData.data
            : [];
        this.hideLoader();
      } catch (error) {
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    checkChangesMadeOnThisStep() {
      return this.changesMadeInStep;
    },

    chooseIncludeType(type) {
      this.currentIncludeType = type;
      if (type === 'list') {
        this.elements = this.lists;
      } else if (type === 'tag') {
        this.elements = this.tags;
      } else if (type === 'segment') {
        this.elements = this.segments;
      }
    },

    async validate() {
      let isValid = !this.$v.localModel.subscribeoption.$invalid;
      if (!isValid) {
        await this.showErrorMessage(
          'frontend_groovemail_campaigns_create_select_included_to_proceed'
        );
      } else if (
        !this.localModel.filledemails ||
        this.localModel.filledemails === 0
      ) {
        isValid = false;
        await this.showErrorMessage(
          'frontend_groovemail_campaign_no_recipients_selected'
        );
      }
      this.$emit('on-validate', isValid);
      return isValid;
    },

    // create validation that rturn false or true
    validateBack() {
      let isValid;

      if (!this.localModel.filledemails || this.localModel.filledemails === 0) {
        isValid = false;
      } else {
        isValid = true;
      }

      return isValid;
    },

    async validateForSaveAndExit() {
      const isValid = !this.$v.localModel.subscribeoption.$invalid;
      if (!isValid) {
        await this.showErrorMessage(
          'frontend_groovemail_campaigns_create_select_included_to_proceed'
        );
      }
      return isValid;
    },

    openCreateListModal() {
      this.mail_list_data.name = '';
      this.mail_list_data.description = '';
      this.$root.$emit('modal-open', 'create-maillist-in-campaign-modal');
    },

    async fetchEventsList(listData) {
      // show loader
      this.showLoader();
      
      try {
        await this.getListsData();
        await this.showrecord();

        this.elements = this.lists;
        if (listData && listData.data && listData.data.id) {
          this.localModel.includedElements.push(listData.data.id);
          this.localModel.subscribeoption.push({
            action: 'subscribedlist',
            type: 'list',
            operation: '=',
            id: listData.data.id,
            name: listData.data.name,
          });
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    closeCreateMaillist() {
      this.$root.$emit('modal-close', 'create-maillist-in-campaign-modal');
    },

    openCreateTagModal() {
      this.mail_tag_data.name = '';
      this.mail_tag_data.description = '';
      this.mail_tag_data.category = [];
      this.mail_tag_data.categoryname = '';
      this.$root.$emit('modal-open', 'create-mailtag-in-campaign-modal');
    },

    async fetchEventsTags(tagData) {
      // show loader
      this.showLoader();

      try {
        await this.getTagsData();
        await this.showrecord();

        this.elements = this.tags;
        if (tagData && tagData.data && tagData.data.id) {
          this.localModel.includedElements.push(tagData.data.id);
          this.localModel.subscribeoption.push({
            action: 'hastag',
            type: 'tag',
            operation: '=',
            id: tagData.data.id,
            name: tagData.data.name,
          });
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    closeCreateMailtag() {
      this.$root.$emit('modal-close', 'create-mailtag-in-campaign-modal');
    },

    openCreateSegmentModal(segmentData) {
      if (segmentData) {
        if (this.canCreateCustomSegment) {
          this.segmentCreationCustom = true;
          this.includedConditions = [...segmentData];
          this.includeType = 'OR';
        } else {
          this.showErrorMessage(
            'frontend_groovemail_broadcasts_create_custom_segment_using_segment_error'
          );
          return false;
        }
      } else {
        this.segmentCreationCustom = false;
        this.includedConditions = [];
        this.includeType = 'AND';
      }
      this.excludedConditions = [];
      this.excludeType = 'AND';
      this.$root.$emit('modal-open', 'create-segment-in-campaign-modal');
      this.$v.segmentModel.segmentName.$reset();
      this.segmentModel.segmentName = '';
      return true;
    },

    closeCreateSegmentModal() {
      this.$root.$emit('modal-close', 'create-segment-in-campaign-modal');
    },

    async createNewSegment() {
      if (this.canCreateSegment) {
        this.$v.segmentModel.segmentName.$touch();

        if (!this.$v.segmentModel.segmentName.$invalid) {
          this.showLoader();

          try {
            const mailsegment = new Mailsegment({});
            mailsegment.name = this.segmentModel.segmentName;
            const segmentRes = await mailsegment.save();
            await this.$refs.createFreshSegment.createNewSegment(segmentRes.id);
            await this.getSegmentsData();
            this.elements = this.segments;
            if (segmentRes && segmentRes.id) {
              if (this.segmentCreationCustom) {
                this.localModel.includedElements = [];
                this.localModel.subscribeoption = [];
                this.currentIncludeType = 'segment';
              }
              this.localModel.includedElements.push(segmentRes.id);
              this.$root.$emit('modal-close', 'create-segment-in-campaign-modal');
              this.localModel.subscribeoption.push({
                action: 'subscribedsegment',
                type: 'segment',
                operation: '=',
                id: segmentRes.id,
                name: segmentRes.name,
              });
              await this.showrecord();
            }
            this.hideLoader();

          } catch (error) {
            this.$emit('requestErrorAlert', error.response?.data?.error);
          }
        }
      }
    },

    enableCreateSegment(canCreate) {
      this.canCreateSegment = canCreate;
    },

    async elementSelected(node) {
      if (this.currentIncludeType === 'list') {
        await this.listSelected(node);
      } else if (this.currentIncludeType === 'tag') {
        await this.tagSelected(node);
      } else if (this.currentIncludeType === 'segment') {
        await this.segmentSelected(node);
      }
    },

    async elementRemoved(node) {
      if (
        this.localModel.subscribeoption &&
        this.localModel.subscribeoption.length
      ) {
        const elementIndex = this.localModel.subscribeoption.findIndex(
          (ele) => {
            return ele.id === node.id;
          }
        );
        this.localModel.subscribeoption.splice(elementIndex, 1);
        await this.showrecord();
      }
    },

    async listSelected(node) {
      if (node.id !== 'createListOption1') {
        this.localModel.subscribeoption.push({
          action: 'subscribedlist',
          type: 'list',
          operation: '=',
          id: node.id,
          name: node.label,
        });
        await this.showrecord();
      } else {
        this.openCreateListModal();
      }
    },

    async tagSelected(node) {
      if (node.id !== 'createTagOption1') {
        this.localModel.subscribeoption.push({
          action: 'hastag',
          type: 'tag',
          operation: '=',
          id: node.id,
          name: node.label,
        });
        await this.showrecord();
      } else {
        this.openCreateTagModal();
      }
    },

    async segmentSelected(node) {
      if (node.id !== 'createSegmentOption1') {
        this.localModel.subscribeoption.push({
          action: 'subscribedsegment',
          type: 'segment',
          operation: '=',
          id: node.id,
          name: node.label,
        });
        await this.showrecord();
      } else {
        this.openCreateSegmentModal();
      }
    },

    async showrecord() {
      if (
        this.localModel.subscribeoption &&
        this.localModel.subscribeoption.length
      ) {
        this.showLoader();
        try {
          const { data } = await axios.get('/campaignsleads', {
            params: {
              include_conditions: this.localModel.subscribeoption,
              include_type: 'OR',
              method_type: this.localModel.sendoption,
            },
          });
          this.hideLoader();
          this.assignRecipientsData(
            data.totalleads,
            data.filledemails,
            data.blocked,
            data.totalsend,
            data.remaining_emails
          );
        } catch (error) {
          this.$emit('requestErrorAlert', error.response?.data?.error);
          this.assignRecipientsData();
        }
      } else {
        this.assignRecipientsData();
      }
      // this.setTreeSelectSearchHeight();
    },

    assignRecipientsData(
      total = 0,
      filled = 0,
      blocked = 0,
      send = 0,
      remaining
    ) {
      this.localModel.totalleads = total;
      this.localModel.filledemails = filled;
      this.localModel.blocked = blocked;
      this.localModel.totalsend = send;
      if (remaining) {
        this.localModel.remainingEmails = remaining;
      }
    },

    // setTreeSelectSearchHeight() {
    //   if (document.querySelector('.treeselect-with-search')) {
    //     const treeselectHeight = document.querySelector(
    //       '.treeselect-with-search'
    //     ).offsetHeight;

    //     const treeselectSearch = document.querySelector(
    //       '.vue-treeselect__input-container'
    //     );
    //     treeselectSearch.style.top = `${treeselectHeight + 25}px`;
    //   }
    // },

    showLoader() {
      this.$emit('showLoader');
    },

    hideLoader() {
      this.$emit('hideLoader');
    },

    async showErrorMessage(errorMessage) {
      await showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${this.$t(errorMessage)}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },
  },

  components: {
    Treeselect,
    LeadsIcon,
    ActiveIcon,
    InactiveIcon,
    PaperIcon,
    PlusIcon,
    CreateList,
    CreateTag,
    CreateSegment,
    VuePerfectScrollbar,
  },

  async created() {
    // show loader
    this.showLoader();

    await this.getListsData();
    await this.getTagsData();
    await this.getSegmentsData();
    await this.showrecord();

    if (
      this.localModel.campaignid !== '' &&
      this.localModel.subscribeoption &&
      this.localModel.subscribeoption.length
    ) {
      this.lists.forEach((ele) => {
        this.elements.push(ele);
      });
      this.tags.forEach((ele) => {
        this.elements.push(ele);
      });
      this.segments.forEach((ele) => {
        this.elements.push(ele);
      });
      this.localModel.subscribeoption.forEach((ele) => {
        const listIndex = this.localModel.includedElements.findIndex(
          (listEle) => {
            return ele.id === listEle;
          }
        );
        if (listIndex < 0) {
          this.localModel.includedElements.push(ele.id);
        }
      });
      
    } else {
      // this.setTreeSelectSearchHeight();

      this.currentIncludeType = 'segment';
      this.segments.forEach((ele) => {
        this.elements.push(ele);
      });
    }

    // hide loader
    this.hideLoader();
  },

  computed: {
    includedElementsSelect() {
      return [
        {
          value: 'segment',
          text: this.$t('frontend_groovemail_campains_step3_belong_to_segment'),
        },
        {
          value: 'list',
          text: this.$t('frontend_groovemail_campains_step3_subscribed_tolist'),
        },
        {
          value: 'tag',
          text: this.$t('frontend_groovemail_campains_step3_has_tag'),
        },
      ];
    },

    includeData() {
      const includeData = [];
      if (this.elements && this.elements.length) {
        this.elements.forEach((element) => {
          includeData.push({ id: element.id, label: element.name });
        });
      }
      if (this.currentIncludeType === 'list') {
        const elementIndex = includeData.findIndex((elementEle) => {
          return elementEle.id === 'createListOption1';
        });
        if (elementIndex < 0) {
          includeData.push({
            id: 'createListOption1',
            label: this.$t('frontend_groovemail_mail_list_newlist'),
          });
        }
      } else if (this.currentIncludeType === 'tag') {
        const elementIndex = includeData.findIndex((elementEle) => {
          return elementEle.id === 'createTagOption1';
        });
        if (elementIndex < 0) {
          includeData.push({
            id: 'createTagOption1',
            label: this.$t('frontend_groovemail_mail_tag_new_tag'),
          });
        }
      } else if (this.currentIncludeType === 'segment') {
        const elementIndex = includeData.findIndex((elementEle) => {
          return elementEle.id === 'createSegmentOption1';
        });
        if (elementIndex < 0) {
          includeData.push({
            id: 'createSegmentOption1',
            label: this.$t('frontend_groovemail_segment_new_segment'),
          });
        }
      }
      return includeData;
    },

    canCreateCustomSegment() {
      if (
        this.localModel.subscribeoption &&
        this.localModel.subscribeoption.length
      ) {
        let createSegment = true;
        this.localModel.subscribeoption.forEach((option) => {
          if (option.type === 'segment') {
            createSegment = false;
          }
        });
        return createSegment;
      }
      return false;
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },

    'localModel.subscribeoption.length': function updateStepValidity() {
      this.changesMadeInStep = true;
      this.$emit(
        'stepValidity',
        Boolean(
          this.localModel.subscribeoption &&
            this.localModel.subscribeoption.length
        ),
        'step3'
      );
      this.$emit('recipientsUpdated');
    },

    'localModel.subscribeoption': function updateRecipients() {
      this.changesMadeInStep = true;
      this.$emit(
        'stepValidity',
        Boolean(
          this.localModel.subscribeoption &&
            this.localModel.subscribeoption.length
        ),
        'step3'
      );
    },

    'localModel.includedElements': function removeIncludedElements() {
      this.changesMadeInStep = true;
      if (
        this.localModel.includedElements &&
        this.localModel.includedElements.length
      ) {
        const elementIndex = this.localModel.includedElements.findIndex(
          (ele) => {
            return (
              ele === 'createListOption1' ||
              ele === 'createTagOption1' ||
              ele === 'createSegmentOption1'
            );
          }
        );
        if (elementIndex >= 0) {
          this.localModel.includedElements.splice(elementIndex, 1);
        }
      }
    },
  },
};
</script>

<style>
.gm-campaign-create .gm-step3__picker {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 12px;
  align-items: start;
}

.gm-campaign-create .gm-step3__picker > .gm-step3__type {
  width: 100%;
}

.gm-campaign-create .gm-step3__targets {
  min-width: 0;
}

.gm-campaign-create .gm-step3__count {
  color: var(--gs-foreground);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gm-campaign-create .gs-wizard-card > p.gm-step3__limit-warning {
  color: var(--gs-danger);
}

.gm-campaign-create .gm-step3__segment-builder {
  margin-top: 18px;
}

/* Segment builder (pages/segments/subscribe.vue) embedded in the modal: hide its own stats and gutters. */
#create-segment-wrapper .segments-stats-section {
  display: none;
}

#create-segment-wrapper .excluded-section-wrapper,
#create-segment-wrapper .included-section-wrapper {
  margin: 0;
  padding: 0;
}

#create-segment-wrapper .excluded-section-wrapper > .flex,
#create-segment-wrapper .included-section-wrapper > .flex {
  padding: 0;
}

#create-segment-wrapper .excluded-section-wrapper h4,
#create-segment-wrapper .included-section-wrapper h4 {
  font-size: 14px;
  font-weight: 650;
}

#create-segment-wrapper .included-section-inner-wrapper,
#create-segment-wrapper .excluded-section-inner-wrapper {
  padding-top: 0;
  padding-bottom: 0;
  border: none;
}

#create-segment-wrapper .new-add-btn {
  font-size: 16px;
}

.custom-treeselect .vue-treeselect__menu-container {
  z-index: 9 !important;
}

@media (max-width: 640px) {
  .gm-campaign-create .gm-step3__picker {
    grid-template-columns: 1fr;
  }
}
</style>
